/* eslint-disable @typescript-eslint/no-explicit-any */
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import {
  faChartBar,
  faRocket,
  faCog,
  faExchange,
  faQuestion,
  faLink,
  faPlane,
  faDollarSign,
  faCertificate,
  faSitemap,
  faCloud,
  faEnvelope,
  faUsers,
  faBars,
  faCogs,
  faFileAlt,
  faUser,
  faFileCertificate,
} from '@fortawesome/pro-solid-svg-icons';
import { faImage, faListAlt } from '@fortawesome/pro-regular-svg-icons';
import { cloneDeep } from 'lodash';
import { OPTIMIZELY_ACCREDIBLE_INTEGRATION } from '../../../../ReduxStorage/Optimizely/constants';
import {
  OptimizelyFeature,
  OptimizelyFeaturesList,
} from '../../../../ReduxStorage/Optimizely/state';

interface BaseLink {
  label: string;
  icon: IconProp;
  portalOnly?: true;
  notPortal?: true;
  institution?: string;
  allowNonAdmin?: true;
  optimizelyFlag?: OptimizelyFeature | OptimizelyFeaturesList;
  accredibleOnly?: true;
}

interface InternalLink extends BaseLink {
  action: string;
}

interface ExternalLink extends BaseLink {
  externalLink: string;
}

export function isInternalLink(link: Link): link is InternalLink {
  if ((link as InternalLink).action) {
    return true;
  }
  return false;
}

export type Link = InternalLink | ExternalLink;

export interface Section {
  name: string;
  links: Link[];
  portalOnly?: true;
  allowNonAdmin?: true;
}

export const navigationSections: Section[] = [
  {
    name: 'Settings',
    links: [
      {
        action: 'institutionUsage',
        label: 'Institution usage',
        icon: faChartBar,
      },
      {
        action: 'basic',
        label: 'General',
        icon: faRocket,
      },
      {
        action: 'appearance',
        label: 'Appearance',
        icon: faImage,
      },
      {
        action: 'advanced',
        label: 'Advanced',
        icon: faCog,
      },
      {
        action: 'ltiIntegrations',
        label: 'Integrations',
        icon: faExchange,
      },
    ],
  },
  {
    name: 'Institution Marketing',
    portalOnly: true,
    links: [
      {
        action: 'general',
        label: 'General',
        icon: faRocket,
      },
      {
        action: 'branding',
        label: 'Branding',
        icon: faImage,
      },
      {
        action: 'about',
        label: 'About (Selling Points)',
        icon: faDollarSign,
      },
      {
        action: 'faqs',
        label: 'FAQs',
        icon: faQuestion,
      },
      {
        action: 'footer',
        label: 'Footer Links',
        icon: faLink,
      },
      {
        action: 'landing',
        label: 'Landing Page',
        icon: faPlane,
      },
      {
        action: 'digitalCredentials',
        label: 'Digital Credentials',
        icon: faFileCertificate,
        optimizelyFlag: OPTIMIZELY_ACCREDIBLE_INTEGRATION,
        accredibleOnly: true,
      },
    ],
  },
  {
    name: 'Qualifications',
    portalOnly: true,
    links: [
      {
        action: 'qualifications',
        label: 'Qualifications',
        icon: faCertificate,
      },
      {
        action: 'qualificationsLanding',
        label: 'Landing Page',
        icon: faPlane,
      },
    ],
  },
  {
    name: 'Courses',
    links: [
      {
        action: 'courses',
        label: 'Courses',
        icon: faCloud,
      },
      {
        action: 'courseCategories',
        label: 'Categories',
        icon: faSitemap,
        portalOnly: true,
      },
      {
        action: 'categories',
        label: 'Categories',
        icon: faSitemap,
        notPortal: true,
      },
    ],
  },
  {
    name: 'People',
    allowNonAdmin: true,
    links: [
      {
        action: 'students',
        label: 'Overview',
        icon: faListAlt,
      },
      {
        action: 'invite',
        label: 'Invite People',
        icon: faEnvelope,
      },
      {
        action: 'userGroups',
        label: 'Groups',
        icon: faUsers,
      },
      {
        action: 'institutionReports',
        label: 'Institution Reports',
        institution: 'ausport',
        icon: faBars,
      },
      {
        action: 'progress',
        label: 'Completion Stats',
        allowNonAdmin: true,
        icon: faCogs,
      },
      {
        action: 'userFields',
        label: 'Extra Fields',
        icon: faFileAlt,
      },
    ],
  },
  {
    name: 'Data',
    links: [
      {
        action: 'studentData',
        label: 'Learner data',
        icon: faUser,
      },
    ],
  },
];

export enum Condition {
  IS_SUBSET = 'IS_SUBSET',
  IS_NOT_SUBSET = 'IS_NOT_SUBSET',
  IS_VALUE = 'IS_VALUE',
  IS_NOT_VALUE = 'IS_NOT_VALUE',
}

function isSubsetOfArray(
  subset: any,
  array: typeof subset | Array<typeof subset>
): boolean {
  return []
    .concat(subset || [])
    .every((value: typeof subset) => array.includes(value));
}

function removeElementWhen(
  array: Array<any>,
  property: string,
  value: any,
  condition?: Condition
): void {
  for (let i = array.length - 1; i > -1; i--) {
    if (Object.prototype.hasOwnProperty.call(array[i], property)) {
      switch (condition) {
        case Condition.IS_SUBSET:
          if (isSubsetOfArray(array[i][property], value)) array.splice(i, 1);
          break;
        case Condition.IS_NOT_SUBSET:
          if (!isSubsetOfArray(array[i][property], value)) array.splice(i, 1);
          break;
        case Condition.IS_NOT_VALUE:
          if (array[i][property] !== value) array.splice(i, 1);
          break;
        case Condition.IS_VALUE:
        default:
          if (array[i][property] === value) array.splice(i, 1);
          break;
      }
    }
  }
}

function filterSectionsByOptimizelyFlag(
  object: Record<string, any>,
  property: keyof typeof object,
  value: OptimizelyFeature | OptimizelyFeaturesList
): void {
  // linter complained and requested to change:
  // FROM THIS: for (const key in object) {
  // TO THIS:   Object.keys(object).forEach(key =>
  // however, jsperf states 35-40% less performant due to added call back
  // so I am disabling it
  // eslint-disable-next-line no-restricted-syntax
  for (const key in object) {
    if (Object.prototype.hasOwnProperty.call(object, key)) {
      if (Array.isArray(object[key]))
        removeElementWhen(
          object[key],
          property,
          value,
          Condition.IS_NOT_SUBSET
        );
      else if (typeof object[key] === 'object')
        filterSectionsByOptimizelyFlag(object[key], property, value);
    }
  }
}

export const sectionsWithOptimizely = (
  enabledFeatures: OptimizelyFeaturesList,
  sections: Section[] = navigationSections
): Section[] => {
  const filteredSections = cloneDeep(sections);
  filterSectionsByOptimizelyFlag(
    filteredSections,
    'optimizelyFlag',
    enabledFeatures
  );
  return filteredSections;
};
